import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../util/errors';
import { setCurrentUser, clearCurrentUser, fetchCurrentUserThunk } from './user.duck';

// ================ Async Thunks ================ //

/**
 * Toggle favorite listing for the current user
 * Adds or removes listing ID from user's public data
 */
export const toggleFavoriteThunk = createAsyncThunk(
  'favorites/toggleFavorite',
  async (listingId, { getState, dispatch, extra: sdk, rejectWithValue }) => {
    try {
      const { currentUser } = getState().user;
      
      if (!currentUser) {
        throw new Error('User must be logged in to favorite listings');
      }

      // Get current favorites from user public data
      const currentFavorites = currentUser.attributes.profile.publicData?.favoriteListings || [];
      
      // Check if listing is already favorited
      const isFavorited = currentFavorites.includes(listingId);
      
      // Toggle the favorite
      const updatedFavorites = isFavorited
        ? currentFavorites.filter(id => id !== listingId)
        : [...currentFavorites, listingId];

      // Update user profile with new favorites
      const response = await sdk.currentUser.updateProfile(
        {
          publicData: {
            favoriteListings: updatedFavorites,
          },
        },
        {
          expand: true,
          include: ['profileImage'],
          'fields.image': ['variants.square-small', 'variants.square-small2x'],
        }
      );

      // Extract the updated user from response
      const updatedUser = response.data.data;
      
      // Update current user in the store
      dispatch(setCurrentUser(updatedUser));

      return {
        listingId,
        isFavorited: !isFavorited,
        favorites: updatedFavorites,
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// Backward compatible wrapper
export const toggleFavorite = listingId => (dispatch, getState, sdk) => {
  return dispatch(toggleFavoriteThunk(listingId));
};

/**
 * Get favorite listings
 */
export const getFavoriteListingsThunk = createAsyncThunk(
  'favorites/getFavoriteListings',
  async (_, { getState, extra: sdk, rejectWithValue }) => {
    try {
      const { currentUser } = getState().user;
      
      if (!currentUser) {
        return { listings: [], favoriteIds: [] };
      }

      const favoriteIds = currentUser.attributes.profile.publicData?.favoriteListings || [];
      
      if (favoriteIds.length === 0) {
        return { listings: [], favoriteIds: [] };
      }

      // Fetch listings by IDs
      const listingPromises = favoriteIds.map(id =>
        sdk.listings
          .show({
            id,
            include: ['author', 'images'],
            'fields.image': ['variants.listing-card', 'variants.scaled-small'],
          })
          .then(response => {
            // Extract listing and related data
            const listing = response.data.data;
            const included = response.data.included || [];
            const images = included.filter(inc => inc.type === 'image');
            const author = included.find(inc => inc.type === 'user');
            
            return {
              ...listing,
              images,
              author,
            };
          })
          .catch(e => {
            console.warn(`Failed to fetch listing ${id}:`, e);
            return null;
          })
      );

      const listings = await Promise.all(listingPromises);
      const validListings = listings.filter(listing => listing !== null);

      return { listings: validListings, favoriteIds };
    } catch (e) {
      console.error('Error in getFavoriteListingsThunk:', e);
      return rejectWithValue(storableError(e));
    }
  }
);

// Backward compatible wrapper
export const getFavoriteListings = () => (dispatch, getState, sdk) => {
  return dispatch(getFavoriteListingsThunk());
};

// ================ Slice ================ //

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState: {
    favoriteListings: [],
    favoriteIds: [],
    toggleInProgress: null, // listingId being toggled
    toggleError: null,
    fetchInProgress: false,
    fetchError: null,
  },
  reducers: {
    clearFavoritesError: state => {
      state.toggleError = null;
      state.fetchError = null;
    },
  },
  extraReducers: builder => {
    builder
      // Listen to fetchCurrentUserThunk.fulfilled to sync favoriteIds when user loads
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        const user = action.payload;
        const favoriteListings = user?.attributes?.profile?.publicData?.favoriteListings || [];
        state.favoriteIds = favoriteListings;
      })
      // Listen to setCurrentUser to sync favoriteIds from user's public data
      .addCase(setCurrentUser, (state, action) => {
        const user = action.payload;
        const favoriteListings = user?.attributes?.profile?.publicData?.favoriteListings || [];
        state.favoriteIds = favoriteListings;
      })
      // Listen to clearCurrentUser to reset favorites when user logs out
      .addCase(clearCurrentUser, (state) => {
        state.favoriteIds = [];
        state.favoriteListings = [];
        state.toggleInProgress = null;
        state.toggleError = null;
        state.fetchInProgress = false;
        state.fetchError = null;
      })
      // Toggle favorite
      .addCase(toggleFavoriteThunk.pending, (state, action) => {
        state.toggleInProgress = action.meta.arg; // listingId
        state.toggleError = null;
      })
      .addCase(toggleFavoriteThunk.fulfilled, (state, action) => {
        state.toggleInProgress = null;
        state.favoriteIds = action.payload.favorites;
      })
      .addCase(toggleFavoriteThunk.rejected, (state, action) => {
        console.error('Toggle favorite failed:', action.payload);
        state.toggleInProgress = null;
        state.toggleError = action.payload;
      })
      // Get favorite listings
      .addCase(getFavoriteListingsThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(getFavoriteListingsThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.favoriteListings = action.payload.listings;
        state.favoriteIds = action.payload.favoriteIds;
      })
      .addCase(getFavoriteListingsThunk.rejected, (state, action) => {
        console.error('Fetch favorites failed:', action.payload);
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      });
  },
});

export default favoritesSlice.reducer;

export const { clearFavoritesError } = favoritesSlice.actions;

// ================ Selectors ================ //

export const isFavorite = (state, listingId) => {
  const { currentUser } = state.user;
  if (!currentUser) return false;
  
  const favoriteIds = currentUser.attributes.profile.publicData?.favoriteListings || [];
  return favoriteIds.includes(listingId);
};

export const getFavoriteIds = state => {
  const { currentUser } = state.user;
  if (!currentUser) return [];
  
  return currentUser.attributes.profile.publicData?.favoriteListings || [];
};
